import {
	CTF_BASE_RADIUS,
	CTF_CAPTURES_TO_WIN,
	CTF_FLAG_RADIUS,
	CTF_FLAG_RETURN_TICKS,
	GRAVITY_G,
	GRAVITY_MIN_DISTANCE,
	KOTH_BAND_INNER,
	KOTH_BAND_OUTER,
	KOTH_POINTS_TO_WIN,
	KOTH_TICKS_PER_POINT,
	SURVIVAL_2008_ASTEROID_POINTS,
	SURVIVAL_2008_BONUS_EVERY,
	SURVIVAL_2008_TICKS_PER_POINT,
} from "../constants";
import type { PlayerIndex, Ship } from "../entities";
import type { SimEvent } from "../events";
import type { MapDef } from "../maps";
import * as DMath from "../math/det-math";
import { wrapCoord, wrappedDelta, wrappedDistance } from "../math/torus";
import type { FlagState, ObjectiveState, WorldState } from "../state";
import { findEntity } from "../world";

/**
 * Objective modes (P11.2). Mode state is plain data on the world (`world.objective`), not
 * entities, so collisions, views and netcode need no new kinds: this system runs after the
 * collision and wrap passes and owns the zone (King of the Hill) and the flags (Capture the
 * Flag), including a dropped flag's own little physics (gravity, drift, wrap, planets).
 */

/** The initial objective for a mode, or null for duel and survival. */
export function createObjective(
	world: WorldState,
	map: MapDef,
	survivalScoring: "asteroids" | "2008" = "asteroids",
): ObjectiveState | null {
	const players = world.players.length;
	if (world.mode === "survival" && survivalScoring === "2008") {
		return {
			kind: "survival",
			scores: new Array(players).fill(0),
			aliveTicks: 0,
			nextBonus: SURVIVAL_2008_BONUS_EVERY,
		};
	}
	if (world.mode === "koth") {
		const heaviest = [...map.planets].sort((a, b) => b.mass - a.mass)[0];
		const zone = heaviest
			? {
					x: heaviest.x,
					y: heaviest.y,
					inner: heaviest.radius + KOTH_BAND_INNER,
					outer: heaviest.radius + KOTH_BAND_OUTER,
				}
			: { x: map.size.w / 2, y: map.size.h / 2, inner: 0, outer: KOTH_BAND_OUTER };
		return {
			kind: "koth",
			target: KOTH_POINTS_TO_WIN,
			scores: new Array(players).fill(0),
			zone,
			holdTicks: new Array(players).fill(0),
			holder: null,
		};
	}
	if (world.mode === "duel" && world.rules.scoreLimit !== null) {
		return { kind: "frags", target: world.rules.scoreLimit, scores: new Array(players).fill(0) };
	}
	if (world.mode === "ctf") {
		const flags: FlagState[] = [];
		for (let p = 0; p < players; p++) {
			const sp = map.spawnPoints[p];
			// A one-spawn-point map: the second base mirrors the first through the centre.
			const first = map.spawnPoints[0] ?? { x: 0, y: 0 };
			const home = sp ? { x: sp.x, y: sp.y } : { x: map.size.w - first.x, y: map.size.h - first.y };
			flags.push({
				owner: p,
				home,
				pos: { ...home },
				vel: { x: 0, y: 0 },
				carrier: null,
				atHome: true,
				idleTicks: 0,
			});
		}
		return { kind: "ctf", target: CTF_CAPTURES_TO_WIN, scores: new Array(players).fill(0), flags };
	}
	return null;
}

export function applyObjectives(world: WorldState, events: SimEvent[]): void {
	const objective = world.objective;
	if (!objective) return;
	if (objective.kind === "koth") holdZone(world, objective, events);
	else if (objective.kind === "ctf") runFlags(world, objective, events);
	else if (objective.kind === "frags") countFrags(world, objective, events);
	else scoreSurvival(world, objective, events);
}

/** The player's ship, if one is in the world. */
function shipOf(world: WorldState, player: PlayerIndex): Ship | null {
	const id = world.players[player]?.shipId;
	const e = id == null ? undefined : findEntity(world, id);
	return e?.kind === "ship" ? e : null;
}

/** Is a ship inside the King of the Hill band? */
export function inZone(
	world: WorldState,
	objective: Extract<ObjectiveState, { kind: "koth" }>,
	pos: { x: number; y: number },
): boolean {
	const d = wrappedDistance(pos, objective.zone, world.size);
	return d >= objective.zone.inner && d <= objective.zone.outer;
}

/** One point per KOTH_TICKS_PER_POINT ticks for the only pilot in the zone; contested = nobody. */
function holdZone(
	world: WorldState,
	objective: Extract<ObjectiveState, { kind: "koth" }>,
	events: SimEvent[],
): void {
	const inside: PlayerIndex[] = [];
	for (const p of world.players) {
		const ship = shipOf(world, p.index);
		if (ship && inZone(world, objective, ship.pos)) inside.push(p.index);
	}
	const holder = inside.length === 1 ? (inside[0] as PlayerIndex) : null;
	objective.holder = holder;
	if (holder === null) return;
	const held = (objective.holdTicks[holder] ?? 0) + 1;
	if (held < KOTH_TICKS_PER_POINT) {
		objective.holdTicks[holder] = held;
		return;
	}
	objective.holdTicks[holder] = 0;
	objective.scores[holder] = (objective.scores[holder] ?? 0) + 1;
	events.push({
		type: "scored",
		player: holder,
		score: objective.scores[holder] ?? 0,
		how: "hold",
	});
}

/** Flag pick-ups, carrying, captures, drops and returns. */
function runFlags(
	world: WorldState,
	objective: Extract<ObjectiveState, { kind: "ctf" }>,
	events: SimEvent[],
): void {
	for (const flag of objective.flags) {
		if (flag.carrier !== null) {
			const carrier = shipOf(world, flag.carrier);
			if (carrier) {
				flag.pos = { x: carrier.pos.x, y: carrier.pos.y };
				flag.vel = { x: carrier.vel.x, y: carrier.vel.y };
				continue;
			}
			// The carrier was destroyed: the flag drops where it was, still drifting.
			events.push({
				type: "flagDropped",
				owner: flag.owner,
				by: flag.carrier,
				pos: { ...flag.pos },
			});
			flag.carrier = null;
			flag.idleTicks = 0;
		}
		if (!flag.atHome) driftFlag(world, flag, events);
	}

	for (const p of world.players) {
		const ship = shipOf(world, p.index);
		if (!ship) continue;
		for (const flag of objective.flags) {
			if (flag.carrier !== null) continue;
			if (wrappedDistance(ship.pos, flag.pos, world.size) > ship.radius + CTF_FLAG_RADIUS) continue;
			if (flag.owner === p.index) {
				if (!flag.atHome) returnHome(flag, events, p.index);
			} else {
				flag.carrier = p.index;
				flag.atHome = false;
				events.push({ type: "flagTaken", owner: flag.owner, by: p.index });
			}
		}
		// Capture: bring an enemy flag to your own base while your flag is home.
		const own = objective.flags[p.index];
		if (!own?.atHome || wrappedDistance(ship.pos, own.home, world.size) > CTF_BASE_RADIUS) continue;
		for (const flag of objective.flags) {
			if (flag.carrier !== p.index) continue;
			returnHome(flag, events, null);
			objective.scores[p.index] = (objective.scores[p.index] ?? 0) + 1;
			events.push({
				type: "scored",
				player: p.index,
				score: objective.scores[p.index] ?? 0,
				how: "capture",
			});
		}
	}
}

function returnHome(flag: FlagState, events: SimEvent[], by: PlayerIndex | null): void {
	flag.pos = { ...flag.home };
	flag.vel = { x: 0, y: 0 };
	flag.carrier = null;
	flag.atHome = true;
	flag.idleTicks = 0;
	events.push({ type: "flagReturned", owner: flag.owner, by });
}

/** A dropped flag falls under planet gravity, wraps, and goes home on a planet or when left too long. */
function driftFlag(world: WorldState, flag: FlagState, events: SimEvent[]): void {
	flag.idleTicks++;
	if (flag.idleTicks >= CTF_FLAG_RETURN_TICKS) {
		returnHome(flag, events, null);
		return;
	}
	flag.pos.x = wrapCoord(flag.pos.x + flag.vel.x, world.size.width);
	flag.pos.y = wrapCoord(flag.pos.y + flag.vel.y, world.size.height);
	for (const e of world.entities) {
		if (e.kind !== "planet") continue;
		const d = wrappedDelta(flag.pos, e.pos, world.size);
		const dist = DMath.hypot(d.x, d.y);
		if (dist <= e.radius) {
			returnHome(flag, events, null);
			return;
		}
		if (dist < GRAVITY_MIN_DISTANCE) continue;
		const k = (GRAVITY_G * e.mass) / (dist * dist * dist);
		flag.vel.x += d.x * k;
		flag.vel.y += d.y * k;
	}
}

/**
 * Score-limit duel: every ship destroyed this tick scores +1 for the player credited with it,
 * or −1 for its own pilot when nobody else gets the credit (planet, asteroid, own shot).
 * Runs after collisions, so the destroyed ships are still in the world (removed next tick).
 */
function countFrags(
	world: WorldState,
	objective: Extract<ObjectiveState, { kind: "frags" }>,
	events: SimEvent[],
): void {
	const tick: SimEvent[] = [];
	for (const ev of events) {
		if (ev.type !== "destroyed" || ev.kind !== "ship") continue;
		const ship = findEntity(world, ev.id);
		if (ship?.kind !== "ship") continue;
		const killer = ev.creditedPlayer;
		const scorer = killer !== null && killer !== ship.player ? killer : ship.player;
		const delta = scorer === ship.player ? -1 : 1;
		objective.scores[scorer] = (objective.scores[scorer] ?? 0) + delta;
		tick.push({
			type: "scored",
			player: scorer,
			score: objective.scores[scorer] ?? 0,
			how: delta > 0 ? "kill" : "crash",
		});
	}
	events.push(...tick);
}

/** 2008 survival: a point per second alive, points per asteroid, a bonus ship per threshold. */
function scoreSurvival(
	world: WorldState,
	objective: Extract<ObjectiveState, { kind: "survival" }>,
	events: SimEvent[],
): void {
	const player = world.players[0];
	if (!player) return;
	let score = objective.scores[0] ?? 0;
	if (shipOf(world, 0)) {
		objective.aliveTicks++;
		if (objective.aliveTicks % SURVIVAL_2008_TICKS_PER_POINT === 0) score++;
	}
	for (const ev of events) {
		if (ev.type === "destroyed" && ev.kind === "asteroid" && ev.creditedPlayer === 0) {
			score += SURVIVAL_2008_ASTEROID_POINTS;
		}
	}
	objective.scores[0] = score;
	while (score >= objective.nextBonus) {
		objective.nextBonus += SURVIVAL_2008_BONUS_EVERY;
		player.shipsLeft++;
		events.push({ type: "bonusShip", player: 0, shipsLeft: player.shipsLeft });
	}
}

/** The flag this player carries, if any (it slows their ship). */
export function carriedFlag(world: WorldState, player: PlayerIndex): FlagState | null {
	const o = world.objective;
	if (o?.kind !== "ctf") return null;
	return o.flags.find((f) => f.carrier === player) ?? null;
}
